import React, { useState } from "react";
import { Card, Button, Form, Alert, Spinner } from "react-bootstrap";
import { authFetch } from "../utils/authFetch";
import * as XLSX from "xlsx";

export default function ImportBalanta() {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [conturiNegasite, setConturiNegasite] = useState("");

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError("");
      setSuccess("");
    }
  };

  const downloadTemplate = () => {
    const headers = [
      ["CONT", "DENUMIRE_CONT", "VALUTA", "SOLD_DEBIT", "SOLD_CREDIT", "RULAJD", "RULAJC"],
      ["1012", "Capital subscris varsat", "LEI", 0, 50000, 0, 0],
      ["5121", "Conturi la banci in lei", "LEI", 25000, 0, 0, 0],
      ["5311", "Casa in lei", "LEI", 5000, 0, 0, 0],
      ["4111.01", "Client Exemplu SRL", "LEI", 12000, 0, 0, 0],
      ["401.01", "Furnizor Exemplu SRL", "LEI", 0, 8000, 0, 0],
      ["121", "Profit si pierdere", "LEI", 0, 14000, 0, 0]
    ];
    const ws = XLSX.utils.aoa_to_sheet(headers);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Balanta");
    XLSX.writeFile(wb, "model_import_balanta_initiala.xlsx");
  };

  const handleUpload = () => {
    if (!file) {
      setError("Te rog selecteaza un fisier Excel mai intai.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setConturiNegasite("");

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const bstr = e.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const rawData = XLSX.utils.sheet_to_json(ws);

        // Normalize keys and filter out empty rows
        const cleanedData = rawData.map(row => {
          const newRow = {};
          Object.keys(row).forEach(key => {
            const normKey = key.toLowerCase().trim().replace(/[\s\-_.]+/g, "_");
            newRow[normKey] = row[key];
          });
          return newRow;
        }).filter(row => {
          const contVal = row.cont || row.cod || row.cod_cont || row.bxnr || row.cont_initial;
          return contVal && String(contVal).toUpperCase() !== "TOTAL";
        });

        if (cleanedData.length === 0) {
          throw new Error("Fisierul Excel este gol sau nu contine date valide.");
        }

        const res = await authFetch("/imports/balanta-initiala", {
          method: "POST",
          body: JSON.stringify({ data: cleanedData })
        });

        if (res.result === "pass") {
          setSuccess("Balanța inițială a fost importată cu succes!");
          if (res.conturi_negasite) {
            setConturiNegasite(res.conturi_negasite);
          }
          setFile(null);
          setFileName("");
        } else {
          setError(res.errorMsg || "Importul a esuat.");
        }
      } catch (err) {
        setError(err.message || "Eroare la procesarea fisierului Excel.");
      } finally {
        setLoading(false);
      }
    };

    reader.readAsBinaryString(file);
  };

  return (
    <Card className="shadow-lg border-0 rounded-4 overflow-hidden mb-4 bg-white">
      <div className="bg-gradient bg-primary p-4 text-white">
        <h3 className="mb-1 fw-bold">Import Balanță Inițială</h3>
        <p className="mb-0 text-white-50 small">Configurează și încarcă soldurile de pornire ale conturilor tale.</p>
      </div>
      <Card.Body className="p-4">
        {error && <Alert variant="danger" className="rounded-3">{error}</Alert>}
        {success && <Alert variant="success" className="rounded-3">{success}</Alert>}
        
        {conturiNegasite && (
          <Alert variant="warning" className="rounded-3">
            <h6 className="fw-bold mb-2">Conturi create automat (nu existau în nomenclator):</h6>
            <code className="text-dark break-word">{conturiNegasite}</code>
          </Alert>
        )}

        <div className="mb-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h5 className="fw-bold text-dark mb-1">Model Excel</h5>
            <p className="text-muted small mb-0">Descarcă modelul oficial pentru a respecta capetele de tabel.</p>
          </div>
          <Button variant="outline-primary" onClick={downloadTemplate} className="rounded-pill px-4">
            Descarcă model Excel
          </Button>
        </div>

        <hr className="my-4 text-black-50" />

        <Alert variant="info">
          Conturile TVA se importă exclusiv cu codurile 4426, 4427 și 4428, fiecare cel mult o dată.
          Analiticele (de exemplu 4428F, 4428C sau 4428.01) și rândurile TVA duplicate blochează importul.
          Corectează fișierul înainte de încărcare.
        </Alert>

        <Form.Group className="mb-4">
          <Form.Label className="fw-bold text-dark mb-2">Selectare fișier import</Form.Label>
          <div className="border border-2 border-dashed border-primary rounded-3 p-4 text-center cursor-pointer position-relative">
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileChange}
              className="position-absolute top-0 start-0 w-100 h-100 opacity-0 cursor-pointer"
            />
            <div className="py-2">
              <i className="bi bi-file-earmark-excel text-primary display-4 mb-2"></i>
              <p className="fw-semibold text-secondary mb-1">
                {fileName || "Trage fișierul aici sau fă click pentru a-l selecta"}
              </p>
              <p className="text-muted small mb-0">Sunt acceptate doar fișiere Excel (.xlsx, .xls)</p>
            </div>
          </div>
        </Form.Group>

        <div className="d-flex justify-content-end gap-2">
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={loading || !file}
            className="rounded-pill px-4 py-2 fw-semibold d-flex align-items-center"
          >
            {loading && <Spinner size="sm" animation="border" className="me-2" />}
            {loading ? "Se importă..." : "Efectuează Import"}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}
